<template>
  <div>
    <!-- LOADING -->
    <loading-dialog
      :show="showLoading"
      :loading-progress="loadProgress"
      @cancel-loading="cancelClass" />
    <stream-wait :show="showStreamWait" />
    <!-- UI -->
    <player-controls
      @cancel-class="$router.push('Likes')"
      @controls-clicked="resetCountDown"
      @seeking-video="seekingVideo($event)"
      @seeking-volume="seekingVolume($event)"
      @play-clicked="togglePlayPause()"
      @mute-clicked="toggleMute"
      :show="controlsEnabled"
      :current-time="currentTime"
      :video-length="videoDuration"
      :current-volume="currentVolume"
      :is-playing="isPlaying"
      :is-muted="isMuted"
      :is-live="videoIsLive" />
    <!-- BUFFER -->
    <buffer-dialog :dialog="bufferDialog" />
    <!-- PLAYER -->
    <div @click="showControls()" id="player-stream" />
    <audio ref="audiostream" />
  </div>
</template>

<script>
import BufferDialog from "./player/BufferDialog";
import LoadingDialog from "./player/LoadingDialog";
import PlayerControls from "./player/PlayerControls";
import StreamWait from "./player/StreamWait";
import PlayerEvents from "./player/PlayerEvents";

export default {
  name: "Player",
  components: {
    BufferDialog,
    LoadingDialog,
    PlayerControls,
    StreamWait
  },
  data() {
    return {
      videoIsLive: false,
      loadProgress: "0",
      showLoading: false,
      bufferDialog: false,
      currentTime: 0,
      currentVolume: 70,
      tempVolume: 50,
      player: null,
      controlsEnabled: false,
      controlsFadeTimer: null,
      seeking: false,
      audioPlayer: null,
      externalAudio: false,
      videoDuration: 0,
      isPlaying: false,
      isMuted: false
    };
  },
  methods: {
    cancelClass() {
      this.$store.dispatch("stopVideo");
      this.showLoading = false;
    },
    seekingVideo($event) {
      if ($event > this.currentTime - 1 && $event < this.currentTime + 1)
        return;
      this.currentTime = $event;
      if (this.externalAudio) {
        this.audioPlayer.currentTime = this.currentTime;
      }
      this.player.seek(this.currentTime);
    },
    seekingVolume($event) {
      this.currentVolume = $event;
      this.player.setVolume($event);
      if (this.externalAudio) {
        this.audioPlayer.volume = $event / 100;
      }
    },
    toggleMute() {
      if (this.player !== null && this.player.isMuted()) {
        this.player.unmute();
        this.currentVolume = this.tempVolume;
      } else if (this.player !== null) {
        this.player.mute();
        this.tempVolume = this.currentVolume;
        this.currentVolume = 0;
      }
    },
    togglePlayPause() {
      if (this.player !== null && this.player.isPlaying()) {
        this.player.pause();
        if (this.externalAudio) {
          this.audioPlayer.pause();
        }
      } else if (this.player !== null) {
        this.player.play();
        if (this.externalAudio) {
          this.audioPlayer.play();
        }
      }
    },
    resetCountDown() {
      clearTimeout(this.controlsFadeTimer);
      this.controlsFadeTimer = setTimeout(() => {
        this.controlsEnabled = false;
      }, 5000);
    },
    showControls() {
      this.controlsEnabled = true;
      this.controlsFadeTimer = setTimeout(() => {
        this.controlsEnabled = false;
      }, 7000);
    },
    initializePlayer() {
      const currentVideo = this.$store.getters.getCurrentVideo;
      const config = {
        key: this.$store.getters.getConfig.bitmovin_key,
        style: {
          controls: false,
          width: "100%"
        }
      };

      const source = {};

      if (currentVideo.type === "live") {
        source.dash = this.$store.getters.getDashUrl;
        config.network = {
          // eslint-disable-next-line no-unused-vars
          preprocessHttpRequest(type, request) {
            if (request.url.indexOf("http:") > -1 && process.env.NODE_ENV === "production") {
              request.url = request.url.replace("http:", "https:");
            }
          },
        };
      } else {
        source.dash = currentVideo.dash;
      }

      if (
        (currentVideo.type !== "live" && currentVideo.drm === undefined) ||
        currentVideo.drm === "clearkey"
      ) {
        source.drm = {
          clearkey: [
            {
              key: this.$store.getters.getConfig.dkey,
              kid: this.$store.getters.getConfig.dkid // optional
            }
          ]
        };
      }

      if (currentVideo.externalAudio) {
        this.externalAudio = true;
        this.audioPlayer = this.$refs.audiostream;
        this.audioPlayer.volume = this.currentVolume / 100;
        this.audioPlayer.src = currentVideo.externalAudioUrl;
      }

      const container = document.getElementById("player-stream");

      this.player = new bitmovin.player.Player(container, config);

      this.player.on(
        bitmovin.player.PlayerEvent.Playing,
        PlayerEvents.playing.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.TimeChanged,
        PlayerEvents.timeChanged.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.StallStarted,
        PlayerEvents.stallStarted.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.StallEnded,
        PlayerEvents.stallEnded.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.Paused,
        PlayerEvents.paused.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.Muted,
        PlayerEvents.muted.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.Unmuted,
        PlayerEvents.unmuted.bind(null, this)
      );
      this.player.on(
        bitmovin.player.PlayerEvent.PlaybackFinished,
        PlayerEvents.playbackFinished.bind(null, this)
      );

      if (currentVideo.type === "live") {
        this.videoIsLive = true;
      }

      this.player.load(source).then(
        () => {
          this.videoDuration = this.player.getDuration();
          this.player.play();
        },
        () => {
          if (this.videoIsLive) {
            this.startLiveTimeoutInterval(source);
          }
        }
      );
    },
    startLiveTimeoutInterval(source) {
      let counter = 0;
      const interval = setInterval(() => {
        if (this.$store.getters.getNextEvent.status === "streaming") {
          clearInterval(interval);
          this.player.load(source).then(
            () => {
              this.player.play();
            },
            () => {
              this.$router.push("Likes");
            }
          );
        }

        if (counter === 60 * 10) {
          clearInterval(interval);
          this.$router.push("Likes");
        }
        counter++;
      }, 1000);
    }
  },
  computed: {
    showStreamWait() {
      if (
        this.videoIsLive &&
        this.$store.getters.getNextEvent &&
        this.$store.getters.getNextEvent.status === "idle"
      ) {
        return true;
      }

      return false;
    }
  },
  beforeDestroy() {
    this.player.destroy();
    this.audioPlayer = null;
    this.$store.dispatch("videoStopped");
    if (this.$store.getters.getDisplayExists) {
      this.$store.dispatch("startDisplayOffTimer");
    }
  },
  beforeCreate() {
    this.$store.dispatch("videoStarted");
    if (this.$store.getters.getDisplayExists) {
      this.$store.dispatch("stopDisplayOffTimer");
    }
  },
  mounted() {
    this.showLoading = true;
    let timeToLoad = 5;
    let loadingCount = 0;
    if (
      this.$store.getters.getDisplayExists &&
      this.$store.getters.getDisplayDriver !== null &&
      this.$store.getters.getDisplayIsOn === false
    ) {
      timeToLoad = this.$store.getters.getDisplayDriver.onTime;
    }

    // Load and then initialize player
    this.interval = setInterval(() => {
      if (loadingCount >= 100) {
        clearInterval(this.interval);
        this.showLoading = false;
        this.initializePlayer();
      } else {
        loadingCount += 100 / timeToLoad;
        this.loadProgress = loadingCount.toFixed(0);
      }
    }, 1000);
  }
};
</script>

<style>
body {
  overflow: hidden !important;
}

#descripPOS {
  margin-top: 400px;
}

#player-stream {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden !important;
}

#headerPOS {
  position: absolute !important;
  top: 0px;
  left: 0px;
  z-index: 2 !important;
  width: 100% !important;
  height: 10% !important;
}

#controlsPOS {
  position: absolute !important;
  top: 0px;
  left: 0px;
  z-index: 1 !important;
  width: 100% !important;
  height: 100% !important;
}

#cardDialog {
  background-color: black;
}

#dialog {
  overflow: hidden;
  background: none !important;
  box-shadow: none;
}

#controlsGrad {
  background: linear-gradient(transparent, black) !important;
}

#headerGrad {
  background: linear-gradient(black, transparent) !important;
}

.slider__thumb {
  transform: translateY(-50%) scale(2) !important;
}

.slider__track__container,
.slider__track,
.slider__track-fill {
  height: 10px !important;
}

.sliderAlignSeek {
  margin-left: 8px !important;
}

.sliderAlignVol {
  margin-bottom: 15px !important;
}

.iconDisable {
  color: dimgray !important;
}

/* Player Own UI */
.bmpui-ui-uicontainer {
  display: none !important;
}
</style>


// WEBPACK FOOTER //
// src/components/Player.vue